import { Helmet } from "react-helmet-async";
import { CANONICAL_ORIGIN, normalizeCanonicalUrl } from "@/lib/canonical";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: object | object[];
  geoRegion?: string;
  geoPlacename?: string;
}

export const SEOHead = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = `${CANONICAL_ORIGIN}/og-image.png`,
  ogType = "website",
  jsonLd,
  geoRegion = "NL",
  geoPlacename,
}: SEOHeadProps) => {
  const fullTitle = `${title} | Theorieaanbieders.nl`;
  const normalizedCanonical = normalizeCanonicalUrl(canonicalUrl);
  const normalizedOgImage = normalizeCanonicalUrl(ogImage);
  
  const defaultJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Theorieaanbieders.nl",
      "url": CANONICAL_ORIGIN,
      "inLanguage": "nl-NL",
      "description": "Vergelijk rijscholen, theoriecursussen en opleiders in Nederland. Alleen actuele aanbieders, klaar voor het CBR-examen 2026.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${CANONICAL_ORIGIN}/rijscholen?zoek={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Theorieaanbieders.nl",
      "url": CANONICAL_ORIGIN,
      "logo": `${CANONICAL_ORIGIN}/logo-theorie-aanbieders.png`,
      "description": "Het platform om rijscholen, online theorie en opleiders te vergelijken in Nederland.",
      "sameAs": [
        "https://www.facebook.com/theorieaanbieders",
        "https://www.instagram.com/theorieaanbieders"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "url": `${CANONICAL_ORIGIN}/contact`
      }
    }
  ];

  const structuredData = jsonLd 
    ? Array.isArray(jsonLd) 
      ? [...defaultJsonLd, ...jsonLd]
      : [...defaultJsonLd, jsonLd]
    : defaultJsonLd;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content="index, follow" />
      <meta name="language" content="Dutch" />
      <meta name="author" content="Theorieaanbieders.nl" />
      <meta name="content-language" content="nl" />
      <meta name="geo.region" content={geoRegion} />
      {geoPlacename && <meta name="geo.placename" content={geoPlacename} />}
      <link rel="canonical" href={normalizedCanonical} />

      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={normalizedCanonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={normalizedOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Theorieaanbieders.nl" />
      <meta property="og:locale" content="nl_NL" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={normalizedCanonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={normalizedOgImage} />

      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export const createLocalBusinessJsonLd = (business: {
  name: string;
  description?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  url?: string;
  telephone?: string;
  category?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": business.name,
  "description": business.description,
  "address": business.address && business.city ? {
    "@type": "PostalAddress",
    "streetAddress": business.address,
    "addressLocality": business.city,
    "postalCode": business.postalCode,
    "addressCountry": "NL"
  } : undefined,
  "url": business.url,
  "telephone": business.telephone,
  "category": business.category || "Rijschool"
});

export const createEducationalOrgJsonLd = (org: {
  name: string;
  description?: string;
  url?: string;
  location?: string;
  courseType?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  "name": org.name,
  "description": org.description,
  "url": org.url,
  "address": org.location ? {
    "@type": "PostalAddress",
    "addressLocality": org.location,
    "addressCountry": "NL"
  } : undefined
});

export const createJobPostingJsonLd = (job: {
  title: string;
  description: string;
  datePosted: string;
  validThrough?: string;
  employerName: string;
  location: string;
  employmentType?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "JobPosting",
  "title": job.title,
  "description": job.description,
  "datePosted": job.datePosted,
  "validThrough": job.validThrough,
  "hiringOrganization": {
    "@type": "Organization",
    "name": job.employerName
  },
  "jobLocation": {
    "@type": "Place",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": job.location,
      "addressCountry": "NL"
    }
  },
  "employmentType": job.employmentType || "FULL_TIME"
});

export const createArticleJsonLd = (article: {
  headline: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  authorName?: string;
  image?: string;
  url: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.headline,
  "description": article.description,
  "datePublished": article.datePublished,
  "dateModified": article.dateModified || article.datePublished,
  "author": {
    "@type": "Organization",
    "name": article.authorName || "Theorieaanbieders.nl"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Theorieaanbieders.nl",
    "logo": {
      "@type": "ImageObject",
      "url": `${CANONICAL_ORIGIN}/logo-theorie-aanbieders.png`
    }
  },
  "image": article.image,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": article.url
  }
});

export const createNewsArticleJsonLd = (article: {
  headline: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  authorName?: string;
  image?: string;
  url: string;
  section?: string;
  keywords?: string[];
  sourceName?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "NewsArticle",
  "headline": article.headline,
  "description": article.description,
  "datePublished": article.datePublished,
  "dateModified": article.dateModified || article.datePublished,
  "author": {
    "@type": "Organization",
    "name": article.authorName || "Theorieaanbieders.nl",
    "url": CANONICAL_ORIGIN
  },
  "publisher": {
    "@type": "Organization",
    "name": "Theorieaanbieders.nl",
    "logo": {
      "@type": "ImageObject",
      "url": `${CANONICAL_ORIGIN}/logo-theorie-aanbieders.png`,
      "width": 600,
      "height": 60
    }
  },
  "image": article.image ? {
    "@type": "ImageObject",
    "url": article.image,
    "width": 1200,
    "height": 630
  } : undefined,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": article.url
  },
  "articleSection": article.section || "Verkeer & Rijscholen",
  "keywords": article.keywords?.join(", ") || "verkeer, rijschool, CBR, nieuws",
  "inLanguage": "nl-NL",
  "copyrightYear": new Date().getFullYear(),
  "copyrightHolder": {
    "@type": "Organization",
    "name": "Theorieaanbieders.nl"
  },
  "isAccessibleForFree": true
});

export const createNewsCollectionJsonLd = (collection: {
  name: string;
  description: string;
  url: string;
  items: Array<{
    headline: string;
    url: string;
    datePublished: string;
    image?: string;
  }>;
}) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": collection.name,
  "description": collection.description,
  "url": collection.url,
  "publisher": {
    "@type": "Organization",
    "name": "Theorieaanbieders.nl",
    "logo": {
      "@type": "ImageObject",
      "url": `${CANONICAL_ORIGIN}/logo-theorie-aanbieders.png`
    }
  },
  "mainEntity": {
    "@type": "ItemList",
    "itemListElement": collection.items.slice(0, 10).map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "NewsArticle",
        "headline": item.headline,
        "url": item.url,
        "datePublished": item.datePublished,
        "image": item.image
      }
    }))
  }
});

export const createBreadcrumbJsonLd = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const createItemListJsonLd = (items: { name: string; url: string; description?: string }[], listName: string) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": listName,
  "itemListElement": items.slice(0, 10).map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "url": item.url,
    "description": item.description
  }))
});
